import { X, Copy, Gift } from 'lucide-react';
import { FaFacebookSquare, FaLinkedin } from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { IoLogoWhatsapp } from 'react-icons/io';
import useAlert from 'src/hooks/use-alert';
import { selectUserState } from 'src/lib/features/auth/authSlice';
import { useTypedSelector } from 'src/lib/store/store';

const ReferralModal = ({ open, closeModal }) => {
  const userState = useTypedSelector(selectUserState);
  const { state, open: openAlert, close, Alert } = useAlert();

  const refUrl = userState?.user?.refLink ?? '';

  const message = `Droomwork is my new destination for freelance jobs. Join me on droomwork now to get verified and quality jobs\n\n${refUrl}\n\n`;

  const tags = '#droomwork #talent #client';

  const shareOnTwitter = () => {
    const hashtags = 'droomwork,talents,client';
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      message
    )}&hashtags=${hashtags}`;
    window.open(twitterUrl, '_blank');
  };

  const shareOnWhatsapp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      message + tags
    )}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareOnLinkedIn = () => {
    const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(
      message + tags
    )}&url=${encodeURIComponent(refUrl)}`;
    window.open(linkedinShareUrl, '_blank');
  };

  const shareOnFacebook = () => {
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      refUrl + '\n\n' + message
    )}`;
    window.open(facebookShareUrl, '_blank', 'noopener,noreferrer');
  };

  if (!open) return null;

  const percent =
    parseFloat(process.env.REACT_APP_TALENT_REFERRAL_PERCENT) * 100;

  const socials = [
    { label: 'WhatsApp', onClick: shareOnWhatsapp, icon: <IoLogoWhatsapp className='h-5 w-5' />, color: 'text-green-600' },
    { label: 'LinkedIn', onClick: shareOnLinkedIn, icon: <FaLinkedin className='h-5 w-5' />, color: 'text-blue-600' },
    { label: 'X', onClick: shareOnTwitter, icon: <FaSquareXTwitter className='h-5 w-5' />, color: 'text-black' },
    { label: 'Facebook', onClick: shareOnFacebook, icon: <FaFacebookSquare className='h-5 w-5' />, color: 'text-blue-600' },
  ];

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <button
        type='button'
        aria-label='Close'
        onClick={closeModal}
        className='absolute inset-0 h-full w-full bg-brand-navy/50'
      />

      <div className='relative z-10 flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-card shadow-ui-lg'>
        <div className='flex shrink-0 items-start justify-between gap-3 border-b p-5'>
          <div className='flex min-w-0 items-start gap-3'>
            <span className='grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-orange-100 text-brand-orange'>
              <Gift className='h-4 w-4' />
            </span>
            <div className='min-w-0'>
              <div className='text-[11px] font-semibold uppercase tracking-wider text-brand-orange'>
                Refer &amp; Earn
              </div>
              <h2 className='text-base font-bold text-brand-navy'>
                Invite a friend
              </h2>
            </div>
          </div>
          <button
            type='button'
            onClick={closeModal}
            aria-label='Close'
            className='-mr-2 -mt-2 shrink-0 rounded-full p-2 text-brand-navy transition-colors hover:bg-surface'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto p-5'>
          <p className='text-sm text-muted-foreground'>
            Receive{' '}
            <span className='font-semibold tabular-nums text-brand-navy'>
              {Number.isFinite(percent) ? percent : 0}%
            </span>{' '}
            of your referee&rsquo;s payment inflow upon completion of each
            project milestone.
          </p>

          <div>
            <div className='pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
              Your referral link
            </div>
            <div className='flex items-center gap-3 rounded-lg bg-surface px-3 py-2'>
              <span className='min-w-0 flex-1 truncate font-mono text-xs text-brand-navy'>
                {refUrl}
              </span>
              <button
                type='button'
                aria-label='Copy referral link'
                onClick={() => {
                  navigator.clipboard.writeText(refUrl);
                  openAlert({
                    ...state,
                    title: 'Copied',
                    variant: 'success',
                    active: true,
                  });
                }}
                className='grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-brand-orange'
              >
                <Copy className='h-4 w-4' />
              </button>
            </div>
          </div>

          <div>
            <div className='pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
              Share
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              {socials.map(({ label, onClick, icon, color }) => (
                <button
                  key={label}
                  type='button'
                  onClick={onClick}
                  aria-label={`Share on ${label}`}
                  title={label}
                  className={`grid h-10 w-10 place-items-center rounded-lg border transition-colors hover:border-brand-orange hover:bg-surface ${color}`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        <span className='fixed right-3 top-3 z-30'>
          <Alert />
        </span>
      </div>
    </div>
  );
};

export default ReferralModal;
